import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import {
  getCandidate,
  updateApplicationStatus,
  updateApplicationNotes,
  getCandidateCvStreamUrl,
  verifyCompetency,
  assignHiringManager,
  upsertInterviewer,
  removeInterviewer,
  type ApplicationDto,
  type CandidateDetailDto,
  type VerifiedCompetency,
  type ApplicationInterviewer,
  VERIFICATION_METHOD_LABELS,
  type VerificationMethod,
} from '../../lib/recruitment';
import { listUsers, type UserDto } from '../../lib/users';
import {
  ArrowLeft,
  CheckCircle2,
  CircleDashed,
  UserPlus,
  X,
  ChevronDown,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/rekrut/candidates/$candidateId')({
  component: CandidateDetailPage,
});

function CandidateDetailPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <CandidateDetailContent />
    </ProtectedRoute>
  );
}

const STATUS_META: Record<ApplicationDto['status'], { label: string; variant: 'default' | 'success' | 'destructive' | 'secondary' | 'muted' }> = {
  new:       { label: 'New',       variant: 'secondary' },
  screening: { label: 'Screening', variant: 'default' },
  interview: { label: 'Interview', variant: 'default' },
  offer:     { label: 'Offer',     variant: 'success' },
  hired:     { label: 'Hired',     variant: 'success' },
  rejected:  { label: 'Rejected',  variant: 'destructive' },
};

const STAGES: ApplicationDto['status'][] = ['new', 'screening', 'interview', 'offer', 'hired', 'rejected'];

// ─── Notes Cell ──────────────────────────────────────────────────────────────
function NotesCell({
  applicationId,
  companyId,
  initialNotes,
  canEdit,
}: {
  applicationId: string;
  companyId: string;
  initialNotes: string;
  canEdit: boolean;
}) {
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleChange(val: string) {
    setNotes(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaving(true);
      await updateApplicationNotes(applicationId, val, companyId);
      setSaving(false);
    }, 800);
  }

  return (
    <div className="relative">
      <textarea
        rows={2}
        disabled={!canEdit}
        value={notes}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Add HR notes…"
        className="w-full text-xs border rounded px-2 py-1.5 resize-none bg-background focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60 disabled:cursor-default"
      />
      {saving && <span className="absolute top-1 right-2 text-[10px] text-muted-foreground">saving…</span>}
    </div>
  );
}

// ─── Verification Popover ────────────────────────────────────────────────────
function VerifyPopover({
  code,
  claimedLevel,
  existingVerification,
  applicationId,
  companyId,
  verifiedByUserId,
  onSaved,
  onClose,
}: {
  code: string;
  claimedLevel: number;
  existingVerification?: VerifiedCompetency;
  applicationId: string;
  companyId: string;
  verifiedByUserId: string;
  onSaved: (updated: ApplicationDto) => void;
  onClose: () => void;
}) {
  const [verifiedLevel, setVerifiedLevel] = useState(existingVerification?.verifiedLevel ?? claimedLevel);
  const [method, setMethod] = useState<VerificationMethod>(existingVerification?.method ?? 'technical_interview');
  const [notes, setNotes] = useState(existingVerification?.notes ?? '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    const res = await verifyCompetency({
      applicationId,
      companyId,
      code,
      claimedLevel,
      verifiedLevel,
      method,
      verifiedByUserId,
      notes: notes.trim() || undefined,
    });
    setSaving(false);
    if (res.success && res.data) {
      toast.success(`${code} verified at L${verifiedLevel}`);
      onSaved(res.data.application);
      onClose();
    } else {
      toast.error(res.error ?? 'Failed to save verification');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-background border rounded-xl shadow-xl p-5 w-80 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">
            Verify <span className="font-mono text-blue-700">{code}</span>
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="text-xs text-muted-foreground bg-muted/50 rounded px-2.5 py-1.5">
          Claimed (CV): <span className="font-semibold text-foreground">L{claimedLevel}</span>
        </div>

        {/* Verified level */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Verified Level</label>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((l) => (
              <button
                key={l}
                onClick={() => setVerifiedLevel(l)}
                className={`flex-1 py-1.5 rounded text-xs font-semibold border transition-colors ${
                  verifiedLevel === l
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-border text-muted-foreground hover:border-green-400 hover:text-green-700'
                }`}
              >
                L{l}
              </button>
            ))}
          </div>
        </div>

        {/* Method */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Assessment Method</label>
          <div className="relative">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as VerificationMethod)}
              className="w-full text-xs border rounded px-2.5 py-2 bg-background appearance-none pr-7 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {(Object.entries(VERIFICATION_METHOD_LABELS) as [VerificationMethod, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-2.5 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Notes (optional)</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Passed system design round…"
            className="w-full text-xs border rounded px-2.5 py-1.5 resize-none bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" className="flex-1 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Verification'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Interviewer Panel ───────────────────────────────────────────────────────
function InterviewerPanel({
  application,
  companyId,
  canManage,
  users,
  onUpdated,
}: {
  application: ApplicationDto;
  companyId: string;
  canManage: boolean;
  users: UserDto[];
  onUpdated: (updated: ApplicationDto) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStage, setSelectedStage] = useState<ApplicationDto['status']>('interview');
  const [saving, setSaving] = useState(false);

  const interviewers = application.interviewers ?? [];

  async function handleAdd() {
    if (!selectedUserId) return;
    const user = users.find((u) => u.id === selectedUserId);
    if (!user) return;
    setSaving(true);
    const res = await upsertInterviewer({
      applicationId: application.id,
      companyId,
      userId: user.id,
      name: user.name,
      role: selectedRole.trim() || undefined,
      stage: selectedStage,
    });
    setSaving(false);
    if (res.success && res.data) {
      onUpdated(res.data.application);
      setAdding(false);
      setSelectedUserId('');
      setSelectedRole('');
    } else {
      toast.error(res.error ?? 'Failed to add interviewer');
    }
  }

  async function handleRemove(userId: string, stage: ApplicationDto['status']) {
    const res = await removeInterviewer({ applicationId: application.id, companyId, userId, stage });
    if (res.success && res.data) {
      onUpdated(res.data.application);
    } else {
      toast.error(res.error ?? 'Failed to remove');
    }
  }

  // Group interviewers by stage
  const byStage = STAGES.reduce<Record<string, ApplicationInterviewer[]>>((acc, s) => {
    const list = interviewers.filter((i) => i.stage === s);
    if (list.length > 0) acc[s] = list;
    return acc;
  }, {});

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Interviewers</p>
        {canManage && (
          <button
            onClick={() => setAdding((v) => !v)}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Add
          </button>
        )}
      </div>

      {Object.entries(byStage).map(([stage, list]) => (
        <div key={stage} className="space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {STATUS_META[stage as ApplicationDto['status']].label}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {list.map((iv) => (
              <span
                key={`${iv.userId}-${iv.stage}`}
                className="flex items-center gap-1 text-xs bg-violet-50 text-violet-700 border border-violet-100 px-2 py-0.5 rounded-full"
              >
                {iv.name}
                {iv.role && <span className="text-violet-400">· {iv.role}</span>}
                {canManage && (
                  <button
                    onClick={() => handleRemove(iv.userId, iv.stage)}
                    className="ml-0.5 text-violet-400 hover:text-violet-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      ))}

      {interviewers.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground italic">No interviewers assigned yet.</p>
      )}

      {adding && (
        <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="w-full text-xs border rounded px-2 py-1.5 bg-background appearance-none pr-6 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Select person…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                ))}
              </select>
              <ChevronDown className="absolute right-1.5 top-2 w-3 h-3 text-muted-foreground pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value as ApplicationDto['status'])}
                className="text-xs border rounded px-2 py-1.5 bg-background appearance-none pr-6 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>{STATUS_META[s].label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-1.5 top-2 w-3 h-3 text-muted-foreground pointer-events-none" />
            </div>
          </div>
          <input
            type="text"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            placeholder="Role/title (optional, e.g. Technical Lead)"
            className="w-full text-xs border rounded px-2 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" className="flex-1 text-xs" onClick={handleAdd} disabled={saving || !selectedUserId}>
              {saving ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hiring Manager Row ──────────────────────────────────────────────────────
function HiringManagerRow({
  application,
  companyId,
  canManage,
  users,
  onUpdated,
}: {
  application: ApplicationDto;
  companyId: string;
  canManage: boolean;
  users: UserDto[];
  onUpdated: (updated: ApplicationDto) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(application.hiringManagerUserId ?? '');
  const [saving, setSaving] = useState(false);

  const currentHM = users.find((u) => u.id === application.hiringManagerUserId);

  async function handleSave() {
    setSaving(true);
    const res = await assignHiringManager({
      applicationId: application.id,
      companyId,
      hiringManagerUserId: selectedUserId || null,
    });
    setSaving(false);
    if (res.success && res.data) {
      onUpdated(res.data.application);
      setEditing(false);
      toast.success('Hiring manager updated');
    } else {
      toast.error(res.error ?? 'Failed to update hiring manager');
    }
  }

  return (
    <div className="flex items-center gap-2 mt-3">
      <Shield className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <p className="text-xs text-muted-foreground">Hiring Manager:</p>
      {editing ? (
        <div className="flex items-center gap-1.5 flex-1">
          <div className="relative flex-1">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full text-xs border rounded px-2 py-1 bg-background appearance-none pr-6 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">(none)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-1.5 top-1.5 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
          <Button size="sm" className="text-xs h-6 px-2" onClick={handleSave} disabled={saving}>
            {saving ? '…' : 'Save'}
          </Button>
          <Button variant="ghost" size="sm" className="text-xs h-6 px-1.5" onClick={() => setEditing(false)}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          {currentHM ? (
            <span className="text-xs font-medium text-foreground">{currentHM.name}</span>
          ) : (
            <span className="text-xs text-muted-foreground italic">Not assigned</span>
          )}
          {canManage && (
            <button onClick={() => setEditing(true)} className="text-[10px] text-primary hover:underline">
              {currentHM ? 'Change' : 'Assign'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Competency Verification Section (inside an application card) ────────────
function CompetencyVerificationSection({
  candidateCompetencies,
  application,
  companyId,
  canManage,
  verifiedByUserId,
  onUpdated,
}: {
  candidateCompetencies: Array<{ code: string; level: number; confidence?: number }>;
  application: ApplicationDto;
  companyId: string;
  canManage: boolean;
  verifiedByUserId: string;
  onUpdated: (updated: ApplicationDto) => void;
}) {
  const [popover, setPopover] = useState<{ code: string; claimedLevel: number } | null>(null);
  const verifiedMap = new Map(
    (application.verifiedCompetencies ?? []).map((v) => [v.code, v]),
  );

  // Only show competencies if there are any
  if (candidateCompetencies.length === 0) return null;

  const openPopover = popover
    ? application.verifiedCompetencies?.find((v) => v.code === popover.code)
    : undefined;

  return (
    <div className="mt-4">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
        Competency Assessment
      </p>

      {/* Two-column layout: Claimed vs Verified */}
      <div className="grid grid-cols-2 gap-3">
        {/* Claimed column */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Claimed (CV)
          </p>
          <div className="flex flex-wrap gap-1">
            {candidateCompetencies.map((comp) => {
              const isVerified = verifiedMap.has(comp.code);
              return (
                <button
                  key={comp.code}
                  disabled={!canManage}
                  onClick={() => setPopover({ code: comp.code, claimedLevel: comp.level })}
                  title={canManage ? `Click to verify ${comp.code}` : undefined}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                    isVerified
                      ? 'bg-blue-50 text-blue-500 border-blue-100 line-through opacity-60'
                      : 'bg-blue-50 text-blue-700 border-blue-100 hover:border-blue-400'
                  } ${canManage ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {comp.code} · L{comp.level}
                  {comp.confidence !== undefined && (
                    <span className="ml-1 text-blue-400">{Math.round(comp.confidence * 100)}%</span>
                  )}
                </button>
              );
            })}
          </div>
          {canManage && (
            <p className="text-[10px] text-muted-foreground/60 italic">Click chip to verify</p>
          )}
        </div>

        {/* Verified column */}
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            Verified
          </p>
          {verifiedMap.size === 0 ? (
            <p className="text-xs text-muted-foreground italic">None verified yet</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {Array.from(verifiedMap.values()).map((v) => (
                <button
                  key={v.code}
                  disabled={!canManage}
                  onClick={() => setPopover({ code: v.code, claimedLevel: v.claimedLevel })}
                  title={`${VERIFICATION_METHOD_LABELS[v.method]}${v.notes ? ` — ${v.notes}` : ''}`}
                  className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200 ${
                    canManage ? 'cursor-pointer hover:border-green-400' : 'cursor-default'
                  }`}
                >
                  <CheckCircle2 className="w-2.5 h-2.5 text-green-600 shrink-0" />
                  {v.code} · L{v.verifiedLevel}
                  <span className="text-green-500 text-[10px] ml-0.5">
                    {VERIFICATION_METHOD_LABELS[v.method].split(' ')[0]}
                  </span>
                </button>
              ))}
            </div>
          )}
          {/* Unverified chips */}
          {(() => {
            const unverified = candidateCompetencies.filter((c) => !verifiedMap.has(c.code));
            if (unverified.length === 0) return null;
            return (
              <div className="flex flex-wrap gap-1 mt-1">
                {unverified.map((c) => (
                  <span
                    key={c.code}
                    className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground/50"
                  >
                    <CircleDashed className="w-2.5 h-2.5 shrink-0" />
                    {c.code}
                  </span>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {popover && (
        <VerifyPopover
          code={popover.code}
          claimedLevel={popover.claimedLevel}
          existingVerification={openPopover}
          applicationId={application.id}
          companyId={companyId}
          verifiedByUserId={verifiedByUserId}
          onSaved={onUpdated}
          onClose={() => setPopover(null)}
        />
      )}
    </div>
  );
}

// ─── Main Content ────────────────────────────────────────────────────────────
function CandidateDetailContent() {
  const { candidateId } = Route.useParams();
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:rekrut');

  const [detail, setDetail] = useState<CandidateDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<UserDto[]>([]);

  function handleDownloadCv() {
    if (!detail) return;
    const url = getCandidateCvStreamUrl(detail.candidate.id, companyId);
    if (!url) { toast.error('Not authenticated'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  useEffect(() => {
    let cancelled = false;
    getCandidate(candidateId, companyId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setError(res.error ?? 'Failed to load candidate');
      }
    });
    // Load users for interviewer/HM pickers (non-blocking)
    if (canManage) {
      listUsers(companyId, { isActive: true, limit: 200 }).then((res) => {
        if (!cancelled && res.success && res.data) {
          setUsers(res.data.users);
        }
      });
    }
    return () => { cancelled = true; };
  }, [candidateId, companyId, canManage]);

  async function handleStatusChange(appId: string, newStatus: ApplicationDto['status']) {
    const res = await updateApplicationStatus(appId, newStatus, companyId);
    if (res.success && res.data && detail) {
      toast.success('Status updated');
      updateApplication(appId, res.data.application);
    } else {
      toast.error(res.error ?? 'Failed to update status');
    }
  }

  function updateApplication(appId: string, updated: ApplicationDto) {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((item) =>
              item.application.id === appId ? { ...item, application: updated } : item,
            ),
          }
        : prev,
    );
  }

  if (loading) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        <div className="h-8 w-40 bg-muted rounded animate-pulse" />
        <div className="h-32 bg-muted rounded-xl animate-pulse" />
        <div className="h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground text-sm">{error || 'Candidate not found'}</p>
        <Link to="/rekrut/candidates/" className="text-sm text-primary underline mt-2 block">Back to Candidates</Link>
      </div>
    );
  }

  const { candidate, applications } = detail;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      {/* Back */}
      <Link to="/rekrut/candidates/">
        <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2">
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Candidates
        </Button>
      </Link>

      {/* Profile card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center text-2xl font-bold text-purple-700 shrink-0">
              {candidate.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground">{candidate.name}</h1>
              <p className="text-sm text-muted-foreground">{candidate.email}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Added {new Date(candidate.createdAt).toLocaleDateString()}
              </p>
            </div>
            {candidate.cvUrl ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadCv}
                className="shrink-0 flex items-center gap-1.5 text-xs"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download CV
              </Button>
            ) : (
              <div className="shrink-0 flex items-center gap-1.5 text-xs text-muted-foreground border border-dashed border-muted-foreground/30 rounded-md px-2.5 py-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                No CV on file
              </div>
            )}
          </div>

          {/* Claimed competency chips — candidate-level, CV source */}
          {candidate.competencyProfile.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Claimed Competencies (from CV)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {candidate.competencyProfile.map((comp) => (
                  <span
                    key={comp.code}
                    className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full"
                  >
                    {comp.code} · L{comp.level}
                    {comp.confidence !== undefined && (
                      <span className="ml-1 text-blue-400">{Math.round(comp.confidence * 100)}%</span>
                    )}
                  </span>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground/60 mt-1.5 italic">
                Unverified — extracted by AI from CV. Verify per application below.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Applications */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-3">
          Applications ({applications.length})
        </h2>
        {applications.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No applications yet.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {applications.map(({ application, job }) => {
              const meta = STATUS_META[application.status];
              const verifiedCount = (application.verifiedCompetencies ?? []).length;
              const totalCount = candidate.competencyProfile.length;
              return (
                <Card key={application.id}>
                  <CardContent className="py-4">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
                        <p className="text-xs text-muted-foreground">{job.department}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          <span className={`font-medium ${verifiedCount > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                            {Math.round(application.matchScore * 100)}% match
                            {verifiedCount > 0 && <span className="ml-1 text-[10px] text-green-500">(verified)</span>}
                          </span>
                          <span>Applied {new Date(application.appliedAt).toLocaleDateString()}</span>
                          {verifiedCount > 0 && (
                            <span className="text-green-600">
                              {verifiedCount}/{totalCount} verified
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-2">
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                        {canManage && (
                          <select
                            value={application.status}
                            onChange={(e) => handleStatusChange(application.id, e.target.value as ApplicationDto['status'])}
                            className="text-xs border rounded px-1.5 py-1 bg-background text-foreground focus:outline-none"
                          >
                            {STAGES.map((s) => (
                              <option key={s} value={s}>{STATUS_META[s].label}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* Hiring manager */}
                    <HiringManagerRow
                      application={application}
                      companyId={companyId}
                      canManage={canManage}
                      users={users}
                      onUpdated={(updated) => updateApplication(application.id, updated)}
                    />

                    {/* Competency verification */}
                    <CompetencyVerificationSection
                      candidateCompetencies={candidate.competencyProfile}
                      application={application}
                      companyId={companyId}
                      canManage={canManage}
                      verifiedByUserId={user?.id ?? ''}
                      onUpdated={(updated) => updateApplication(application.id, updated)}
                    />

                    {/* Interviewers */}
                    <InterviewerPanel
                      application={application}
                      companyId={companyId}
                      canManage={canManage}
                      users={users}
                      onUpdated={(updated) => updateApplication(application.id, updated)}
                    />

                    {/* HR Notes */}
                    <div className="mt-4">
                      <p className="text-xs font-medium text-muted-foreground mb-1">HR Notes</p>
                      <NotesCell
                        applicationId={application.id}
                        companyId={companyId}
                        initialNotes={application.notes ?? ''}
                        canEdit={canManage}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
